import { IDocxTemplate } from "@mctypes/global.types";
import { IAzureBlobFile } from "@schemas/azureSchema";
import { IIssueApi } from "@schemas/issueSchema";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { usePostProjectByProjectIdIssuesGenerateDocxMutation } from "../../../../../openapi";
import { apiSlice } from "../../../../apiSlice";
import { DocxReport, DocxReportDropdown, FailedAlert, MCLoader } from "../../../../components";
import { setShowSuccessMessageThunk } from "../../../../global/globalSlice";
import { useGetOfficalMulticonsultIssueTempaltesQuery } from "../../../../globalRTK";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { ToastMessagesEnums } from "../../../../mc-shared/sharedConstants";
import {
  useDeleteProjectIssueDocxTemplateMutation,
  useGetProjectIssueboardDocxTemplatesQuery,
  useGetProjectIssueDocxTemplatesQuery,
  useUploadIssueboardDocxMutation,
} from "../../../projectRTK";
import { getProjectIdFromUrl } from "../../../utils";
import { downloadAzureBlobFile } from "../../admin/restQueries";
import { TemplateLevelEnum } from "../../checklist/constants";
import { doUploadIssueDocxTemplate } from "../issueAPIDepricated";
import { ISSUE_DOCX_DEMO_DATA } from "../issueConstants";
import { selectFiltredIssuesIds } from "../issueSelectors";
import { getIssueBoardIdFromUrl } from "../issueUtils";

const IssueDocxTemplates: React.FC<{ gridRef: React.RefObject<AgGridReact> }> = ({ gridRef }) => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const [docxModalOpen, setDocxModalOpen] = useState<boolean>(false);
  const projectId = getProjectIdFromUrl(history.location.pathname);
  const [uploadIssueboardTemplate, { isError: errorUploadIssueboardTemplate, isLoading: uploadLoading }] =
    useUploadIssueboardDocxMutation();
  const [deleteTemplate, { isError: deleteTemplateError, isLoading: deleteTemplateLoading }] =
    useDeleteProjectIssueDocxTemplateMutation();
  const [postIssuesGenerateDocx, { isError: errorGeneratingDocx, isLoading: isGeneratingDocx }] =
    usePostProjectByProjectIdIssuesGenerateDocxMutation();

  const issueBoardId = getIssueBoardIdFromUrl(history.location.pathname);

  const {
    data: projectIssueboardDocxTemplates = [],
    isLoading: isLoadingProjectIssueboardTemplates,
    isError: getProjectIssueboardTemplatesError,
  } = useGetProjectIssueboardDocxTemplatesQuery({ projectId, issueBoardId });

  const {
    data: projectIssueDocxTemplates = [],
    isLoading: isLoadingProjectIssueTemplates,
    isError: getProjectIssueTemplatesError,
  } = useGetProjectIssueDocxTemplatesQuery({ projectId });

  const {
    data: officialIssueTemplates = [],
    isLoading: isLoadingIssueTemplates,
    isError: getIssueTemplatesError,
  } = useGetOfficalMulticonsultIssueTempaltesQuery("");

  const projectIssueboardDocxTemplatesFormatted = useMemo(() => {
    return projectIssueboardDocxTemplates.map((template) => {
      return {
        _id: template.blobName,
        fileName: template.fileName,
        isMulticonsult: false,
        uploadedDate: new Date(template.uploadedAt),
        uploadedBy: {
          name: template.uploadedBy,
        },
        templateLevel: TemplateLevelEnum.BOARD,
      };
    });
  }, [projectIssueboardDocxTemplates]);

  const projectDocxTemplatesFormatted = useMemo(() => {
    return projectIssueDocxTemplates.map((template) => {
      return {
        _id: template.blobName,
        fileName: template.fileName,
        isMulticonsult: false,
        uploadedDate: new Date(template.uploadedAt),
        uploadedBy: {
          name: template.uploadedBy,
        },
        templateLevel: TemplateLevelEnum.PROJECT,
      };
    });
  }, [projectIssueDocxTemplates]);

  const officalDocxTemplatesFormatted = officialIssueTemplates.map((template) => {
    const docxTemplate: IDocxTemplate = {
      _id: template.blobName,
      fileName: template.fileName,
      isMulticonsult: true,
      uploadedDate: new Date(template.uploadedAt),
      uploadedBy: {
        name: template.uploadedBy,
      },
      templateLevel: TemplateLevelEnum.MULTICONSULT,
    };
    return docxTemplate;
  });

  const docxTemplates = [
    ...officalDocxTemplatesFormatted,
    ...projectDocxTemplatesFormatted,
    ...projectIssueboardDocxTemplatesFormatted,
  ];

  const uploadDocxTempalte = async (event: React.ChangeEvent<HTMLInputElement>, level: string) => {
    if (level === TemplateLevelEnum.PROJECT) {
      await doUploadIssueDocxTemplate(projectId, event);
      dispatch(apiSlice.util.invalidateTags(["IssueBoard"]));
    } else if (level === TemplateLevelEnum.BOARD) {
      await uploadIssueboardTemplate({
        projectId: projectId,
        boardId: issueBoardId,
        event: event,
      }).unwrap();
    }
  };

  const getIssueIds = (issues: IIssueApi[]) => {
    let rowIds: string[] = [];
    if (gridRef?.current?.api) {
      gridRef?.current?.api.forEachNodeAfterFilter((rowNode) => {
        if (rowNode.data?._id) {
          rowIds.push(rowNode.data._id);
        }
      });
    } else rowIds = issues.map((issue) => issue._id);
    return rowIds;
  };

  const issueIds = useAppSelector(selectFiltredIssuesIds);

  const generateIssueDocx = async (docxTemplate: IDocxTemplate) => {
    const selectedDocxTemplate = [
      ...officialIssueTemplates,
      ...projectIssueDocxTemplates,
      ...projectIssueboardDocxTemplates,
    ].find((tmpl) => tmpl.fileName === docxTemplate.fileName) as IAzureBlobFile;

    try {
      const result = await postIssuesGenerateDocx({
        projectId,
        body: {
          issueBoardId,
          templateBlobName: selectedDocxTemplate?.blobName,
          issueIds,
        },
      }).unwrap();

      // 💾 Download the generated DOCX directly
      const link = document.createElement("a");
      link.href = `data:${result.mime};base64,${result.base64}`;
      link.download = result.filename;
      link.click();
    } catch (err) {
      console.error("Failed to generate DOCX:", err);
      dispatch(setShowSuccessMessageThunk("Eksporten feilet"));
    }
  };

  const isErrored =
    getProjectIssueTemplatesError ||
    deleteTemplateError ||
    getProjectIssueboardTemplatesError ||
    errorUploadIssueboardTemplate ||
    errorGeneratingDocx;
  const isLoading =
    isLoadingProjectIssueTemplates ||
    isLoadingIssueTemplates ||
    isLoadingProjectIssueboardTemplates ||
    isGeneratingDocx ||
    uploadLoading ||
    deleteTemplateLoading;
  return (
    <>
      {isLoading && <MCLoader />}
      {isErrored && <FailedAlert />}
      {docxModalOpen === true && (
        <DocxReport
          demoDocxData={ISSUE_DOCX_DEMO_DATA}
          docxTemplates={docxTemplates}
          uploadCompleedCb={() => dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPLOAD_SUCCESFULL))}
          uploadFunc={uploadDocxTempalte}
          generateDocx={generateIssueDocx}
          downloadTemplate={(docxTemplate) => downloadAzureBlobFile(docxTemplate._id, docxTemplate.fileName)}
          deleteDocx={(docxTemplate: IDocxTemplate) =>
            deleteTemplate({
              projectId: projectId,
              fullBlobId: docxTemplate._id,
            })
          }
          closeModal={() => setDocxModalOpen(false)}
        />
      )}
      <DocxReportDropdown
        docxTemplates={docxTemplates}
        generateDocx={generateIssueDocx}
        openDocxModal={() => setDocxModalOpen(true)}
        buttonText="Utskrift"
      />
    </>
  );
};

export default IssueDocxTemplates;
